import { ChangeEvent } from 'react';
import { SamlSource, SsoFormState } from '../../types/forms.type';
import {
  DEFAULT_CUSTOM_CLAIM_CLIENT,
  getCustomClaimClientsError,
  parseCustomClaimClients,
} from '../../utils/customClaimClients';
import AdvancedInput from '../form-inputs/advancedInput';

const SamlSourceOptions: { value: SamlSource; label: string }[] = [
  { value: 'none', label: 'None (reuse existing SSO)' },
  { value: 'url', label: 'Metadata URL' },
  { value: 'xml', label: 'Metadata XML' },
];

function XmlFileUpload({
  id, value, onChange,
}: Readonly<{
  id: string;
  value: string;
  onChange: (xml: string) => void;
}>) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-primary-dark">SAML metadata XML</span>
      <button
        type="button"
        aria-label={value ? 'Replace SAML metadata XML file' : 'Upload SAML metadata XML file'}
        className="flex items-center justify-center gap-2 border-2 border-dashed border-pipgrey-light
          rounded-md p-4 cursor-pointer hover:bg-pipgrey-light text-sm text-piptext-default"
        onClick={() => document.getElementById(id)?.click()}
      >
        <input
          type="file"
          id={id}
          accept=".xml,application/xml,text/xml"
          className="sr-only"
          tabIndex={-1}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            onChange(await file.text());
          }}
        />
        {value
          ? '✓ File loaded — click to replace'
          : 'Click to upload XML file'}
      </button>
      {value && (
        <p className="text-xs text-piptext-default font-mono bg-pipgrey-light rounded px-2 py-1 truncate">
          {value.slice(0, 120)}
          ...
        </p>
      )}
    </div>
  );
}

export default function SsoConfigurationFields({
  idPrefix,
  value,
  onChange,
  isProviderNameLocked = false,
}: Readonly<{
  idPrefix: string;
  value: SsoFormState;
  onChange: (patch: Partial<SsoFormState>) => void;
  isProviderNameLocked?: boolean;
}>) {
  const radioName = `${idPrefix}-saml-source`;

  const samlProviderNameError = !isProviderNameLocked
    && value.samlProviderName
    && !/^[a-zA-Z0-9-]+$/.test(value.samlProviderName)
    ? 'Only letters, numbers, and hyphens are allowed.'
    : null;
  const customClaimClientsError = getCustomClaimClientsError(parseCustomClaimClients(value.customClaimClients));

  return (
    <>
      <AdvancedInput
        type="text"
        id={`${idPrefix}-samlProviderName`}
        label="SAML provider name"
        tooltip="Slug used as the organisation key. Must match the Cognito provider name exactly."
        value={value.samlProviderName}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ samlProviderName: e.target.value })}
        readOnly={isProviderNameLocked}
        placeholder={isProviderNameLocked ? undefined : 'example-org'}
      />
      {samlProviderNameError && (
        <p className="text-pipstatus-error text-xs -mt-2">{samlProviderNameError}</p>
      )}

      <AdvancedInput
        type="text"
        id={`${idPrefix}-customClaimClients`}
        label="Custom client name(s) - Only required for shared SSO configurations"
        tooltip="Optional, comma-separated custom:client SAML claim values for sharing an SSO configuration. Leave blank to default to 'default'."
        value={value.customClaimClients}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ customClaimClients: e.target.value })}
        placeholder={DEFAULT_CUSTOM_CLAIM_CLIENT}
      />
      {customClaimClientsError && (
        <p className="text-pipstatus-error text-xs -mt-2">{customClaimClientsError}</p>
      )}

      <fieldset className="flex flex-col gap-2 border border-pipgrey-light rounded-md p-3">
        <legend className="px-1 text-sm font-semibold text-piptext-default">SAML metadata source</legend>
        <div className="flex gap-4 text-sm">
          {SamlSourceOptions.map((option) => (
            <label className="flex items-center gap-1" htmlFor={`${radioName}-${option.value}`} key={option.value}>
              <input
                type="radio"
                id={`${radioName}-${option.value}`}
                name={radioName}
                checked={value.samlSource === option.value}
                onChange={() => onChange({ samlSource: option.value })}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>

        {value.samlSource === 'url' && (
          <AdvancedInput
            type="text"
            id={`${idPrefix}-metadataUrl`}
            label="SAML metadata URL"
            tooltip="The SAML metadata URL for the organisation's identity provider."
            value={value.metadataUrl}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ metadataUrl: e.target.value })}
            placeholder="https://idp.example.com/saml/metadata"
          />
        )}

        {value.samlSource === 'xml' && (
          <XmlFileUpload
            id={`${idPrefix}-xmlUpload`}
            value={value.xmlMetadata}
            onChange={(xmlMetadata) => onChange({ xmlMetadata })}
          />
        )}
      </fieldset>
    </>
  );
}
